import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  InputGroup,
  Form,
  Pagination,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import {
  useGetFAQQuery,
  useToggleFAQStatusMutation,
  useDeleteFAQMutation,
} from "../../../redux/api/FAQApi/FAQApi";
import DeleteModel from "../../../Components/DeleteModel";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaQuestionCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaList,
  FaTrashAlt,
} from "react-icons/fa";
import "../Services/Services.css"; // Reusing the same premium styles

const FAQ = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const sortBy = searchParams.get("sortBy") || "display_order";
  const order = searchParams.get("order") || "asc";
  const limit = Number(searchParams.get("limit")) || 10;

  const [searchTerm, setSearchTerm] = useState(search);

  const [deleteFAQ] = useDeleteFAQMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState(null);

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  // Debounced search update
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== search) {
        setSearchParams({
          page: 1, // Reset to first page on new search
          limit,
          search: searchTerm,
          sortBy,
          order,
        });
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm, limit, sortBy, order, search, setSearchParams]);

  const { data, isLoading, isFetching } = useGetFAQQuery({
    page,
    limit,
    search,
    sortBy,
    order,
  });

  const [toggleStatus] = useToggleFAQStatusMutation();

  const faqs = data?.faqs || [];
  const totalPages = Math.ceil((data?.total || 0) / limit);

  const handleToggleStatus = async (id) => {
    try {
      await toggleStatus(id).unwrap();
      message.success("Status updated successfully!");
    } catch (error) {
      console.error("Failed to toggle status:", error);
      message.error("Failed to update status.");
    }
  };

  const handleDelete = (faq) => {
    setSelectedFAQ(faq);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteFAQ(selectedFAQ.id).unwrap();
      setShowDeleteModal(false);
      setSelectedFAQ(null);
      message.success("FAQ deleted successfully!");
    } catch (error) {
      console.error("Failed to delete FAQ:", error);
      message.error("Failed to delete FAQ.");
    }
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setSelectedFAQ(null);
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaQuestionCircle className="text-primary me-2" />
            FAQ Management
          </h2>
          <p className="text-muted mb-0">
            Manage your frequently asked questions
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate("/admin/add-faq")}
          className="d-flex align-items-center gap-2 px-4 py-2 shadow-sm rounded-pill fw-semibold"
        >
          <FaPlus /> Add New FAQ
        </Button>
      </div>

      {/* Stats Row */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 bg-white overflow-hidden h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-primary-soft text-primary me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaList size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">{data?.total || 0}</h4>
                <div className="text-muted small fw-semibold">Total FAQs</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 bg-white overflow-hidden h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-success-soft text-success me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaCheckCircle size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">
                  {faqs.filter((f) => f.status === true).length} Active
                </h4>
                <div className="text-muted small fw-semibold">
                  Published FAQs
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 bg-white overflow-hidden h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-danger-soft text-danger me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaTimesCircle size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">
                  {faqs.filter((f) => f.status === false).length} Inactive
                </h4>
                <div className="text-muted small fw-semibold">
                  Unpublished FAQs
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Table Section */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
        <Card.Header className="bg-white border-bottom p-3">
          <Row className="align-items-center">
            <Col md={3} className="d-none d-md-block">
              <h5 className="mb-0 fw-bold px-2">FAQ List</h5>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                className="w-auto cursor-pointer"
                value={limit}
                onChange={(e) =>
                  setSearchParams({
                    page: 1,
                    limit: Number(e.target.value),
                    search,
                    sortBy,
                    order,
                  })
                }
              >
                <option value={10}>Show 10</option>
                <option value={25}>Show 25</option>
                <option value={50}>Show 50</option>
                <option value={100}>Show 100</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <InputGroup className="bg-light rounded-pill border-0 overflow-hidden px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search headings, questions..."
                  className="bg-transparent border-0 shadow-none py-2"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              {isFetching && (
                <div className="text-end text-muted extra-small mt-1 px-3">
                  Updating results...
                </div>
              )}
            </Col>
          </Row>
        </Card.Header>
        <div className="table-responsive">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-muted small text-uppercase fw-bold">
              <tr>
                <th className="ps-4 border-0">S.No</th>
                <th
                  className="border-0 cursor-pointer"
                  onClick={() =>
                    setSearchParams({
                      page: 1,
                      search,
                      sortBy: "question",
                      order: order === "asc" ? "desc" : "asc",
                    })
                  }
                >
                  Question{" "}
                  {sortBy === "question" && (order === "asc" ? "⬆" : "⬇")}
                </th>
                <th
                  className="text-center border-0 cursor-pointer"
                  onClick={() =>
                    setSearchParams({
                      page: 1,
                      search,
                      sortBy: "display_order",
                      order: order === "asc" ? "desc" : "asc",
                    })
                  }
                >
                  Order{" "}
                  {sortBy === "display_order" && (order === "asc" ? "⬆" : "⬇")}
                </th>
                <th
                  className="text-center border-0 cursor-pointer"
                  onClick={() =>
                    setSearchParams({
                      page: 1,
                      search,
                      sortBy: "status",
                      order: order === "asc" ? "desc" : "asc",
                    })
                  }
                >
                  Status {sortBy === "status" && (order === "asc" ? "⬆" : "⬇")}
                </th>
                <th className="text-end pe-4 border-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {faqs && faqs.length > 0 ? (
                faqs.map((f, i) => (
                  <tr key={f.id}>
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark">{i + 1}</div>
                    </td>
                    <td>
                      <div
                        className="text-truncate"
                        style={{ maxWidth: "300px" }}
                        title={f.question}
                      >
                        {f.question}
                      </div>
                    </td>
                    <td className="text-center">
                      <Badge bg="secondary" pill className="fw-normal">
                        {f.display_order || 0}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <div
                        className="cursor-pointer"
                        onClick={() => handleToggleStatus(f.id)}
                      >
                        {f.status ? (
                          <Badge
                            bg="success-light"
                            className="text-success border-0 px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1"
                          >
                            <FaCheckCircle size={10} /> Active
                          </Badge>
                        ) : (
                          <Badge
                            bg="danger-light"
                            className="text-danger border-0 px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1"
                          >
                            <FaTimesCircle size={10} /> Inactive
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-end pe-4">
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="white"
                          size="sm"
                          onClick={() => navigate(`/admin/edit-faq/${f.id}`)}
                          className="btn-action shadow-sm border"
                          title="Edit FAQ"
                        >
                          <FaEdit className="text-primary" />
                        </Button>
                        <Button
                          variant="white"
                          size="sm"
                          onClick={() => handleDelete(f)}
                          className="btn-action shadow-sm border"
                          title="Delete FAQ"
                        >
                          <FaTrashAlt className="text-danger" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div className="py-4">
                      <FaQuestionCircle size={48} className="text-light mb-3" />
                      <h5 className="text-muted fw-bold">No FAQs Found</h5>
                      <p className="text-muted small">
                        {search
                          ? `No results for "${search}"`
                          : "Get started by creating your first FAQ."}
                      </p>
                      {!search && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate("/admin/add-faq")}
                          className="rounded-pill px-4 mt-2"
                        >
                          <FaPlus className="me-2" /> Create FAQ
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Pagination UI */}
      {totalPages > 1 && (
        <Pagination className="justify-content-center my-4 custom-pagination">
          <Pagination.Prev
            disabled={page === 1}
            onClick={() =>
              setSearchParams({
                page: page - 1,
                limit,
                search,
                sortBy,
                order,
              })
            }
          />

          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i + 1}
              active={page === i + 1}
              onClick={() =>
                setSearchParams({
                  page: i + 1,
                  limit,
                  search,
                  sortBy,
                  order,
                })
              }
            >
              {i + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next
            disabled={page === totalPages}
            onClick={() =>
              setSearchParams({
                page: page + 1,
                limit,
                search,
                sortBy,
                order,
              })
            }
          />
        </Pagination>
      )}
      <DeleteModel
        DELETESTATE={showDeleteModal}
        ONCLICK={handleCloseModal}
        YES={handleDeleteConfirm}
        DELETETITLE="Delete FAQ"
        DESCRIPTION={`Are you sure you want to delete this FAQ?`}
        loadTest="Deleting..."
      />
    </div>
  );
};

export default FAQ;
